/**
 * Merge GitLab (username) + Asana (name slug) assignee keys into one NekoHR identity per person.
 *
 * Optional localStorage:
 * - konekoB:assigneeAliases   → { "liam_conroy": "liam_maneki", ... }
 * - konekoB:assigneeDisplayLabels → { "liam_maneki": "Liam Conroy", ... } (overrides default labels)
 */

const ASSIGNEE_ALIASES_STORAGE_KEY = "konekoB:assigneeAliases";
const ASSIGNEE_LABELS_STORAGE_KEY = "konekoB:assigneeDisplayLabels";

/** Default aliases → canonical nekohr / GitLab-style username (edit for your team). */
const DEFAULT_ALIAS_TO_CANONICAL = {
  liam_conroy: "liam_maneki",
  liamconroy: "liam_maneki",
};

/** Display name when we know the canonical key */
const DEFAULT_CANONICAL_LABEL = {
  liam_maneki: "Liam Conroy",
};

function readAliasOverrides() {
  try {
    const raw = localStorage.getItem(ASSIGNEE_ALIASES_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    /** @type {Record<string, string>} */
    const out = {};
    Object.keys(parsed).forEach((k) => {
      const v = parsed[k];
      if (typeof v !== "string") return;
      const from = String(k || "")
        .trim()
        .toLowerCase();
      const to = v.trim().toLowerCase();
      if (from && to) out[from] = to;
    });
    return out;
  } catch {
    return {};
  }
}

function readDisplayLabelOverrides() {
  try {
    const raw = localStorage.getItem(ASSIGNEE_LABELS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    /** @type {Record<string, string>} */
    const out = {};
    Object.keys(parsed).forEach((k) => {
      const v = parsed[k];
      if (typeof v !== "string") return;
      const key = String(k || "")
        .trim()
        .toLowerCase();
      const label = v.trim();
      if (key && label) out[key] = label;
    });
    return out;
  } catch {
    return {};
  }
}

function mergedAliasTable() {
  return {...DEFAULT_ALIAS_TO_CANONICAL, ...readAliasOverrides()};
}

/**
 * @param {string} key
 */
export function canonicalAssigneeKey(key) {
  const k = String(key || "")
    .trim()
    .toLowerCase();
  if (!k || k === "unknown") return k || "unknown";
  const table = mergedAliasTable();
  return table[k] || k;
}

/**
 * @param {string} canonicalKey
 * @param {string} fallbackLabel
 */
export function displayAssigneeLabel(canonicalKey, fallbackLabel) {
  const c = String(canonicalKey || "").toLowerCase();
  const custom = readDisplayLabelOverrides()[c];
  if (custom) return custom;
  if (DEFAULT_CANONICAL_LABEL[c]) return DEFAULT_CANONICAL_LABEL[c];
  return String(fallbackLabel || canonicalKey || "Unknown").trim() || "Unknown";
}

/**
 * @param {import("./canonicalTaskFeed").CanonicalTask} task
 */
export function normalizeTaskAssignee(task) {
  const canonical = canonicalAssigneeKey(task.assigneeKey);
  const label = displayAssigneeLabel(canonical, task.assigneeLabel);
  return {
    ...task,
    assigneeKey: canonical,
    assigneeLabel: label,
  };
}
